import type { Fideicomiso } from "./api";
import { formatFecha, parseFecha } from "./format";

/** Formulario de datos del fideicomiso: todo como texto, tal cual lo escribe el usuario. */
export interface FormFideicomiso {
  nombre: string;
  cuit: string;
  direccion: string;
  fecha_constitucion: string; // dd/mm/aaaa
  fee_porcentaje: string;
  banco: string;
  cbu_alias_ars: string;
  cuenta_usd: string;
  fiduciario_nombre: string;
  fiduciario_cuit: string;
}

export type ErroresFideicomiso = Partial<Record<keyof FormFideicomiso, string>>;

export function formatCuit(digitos: string): string {
  return /^\d{11}$/.test(digitos) ? `${digitos.slice(0, 2)}-${digitos.slice(2, 10)}-${digitos.slice(10)}` : digitos;
}

export function formDesde(f: Fideicomiso): FormFideicomiso {
  return {
    nombre: f.nombre,
    cuit: f.cuit ? formatCuit(f.cuit) : "",
    direccion: f.direccion ?? "",
    fecha_constitucion: f.fecha_constitucion ? formatFecha(f.fecha_constitucion) : "",
    fee_porcentaje: f.fee_porcentaje,
    banco: f.banco ?? "",
    cbu_alias_ars: f.cbu_alias_ars ?? "",
    cuenta_usd: f.cuenta_usd ?? "",
    fiduciario_nombre: f.fiduciario_nombre ?? "",
    fiduciario_cuit: f.fiduciario_cuit ? formatCuit(f.fiduciario_cuit) : "",
  };
}

const soloDigitosCuit = (cuit: string) => cuit.replace(/-/g, "").trim();
const feeNormalizado = (fee: string) => fee.trim().replace(",", ".");

export function validarFideicomiso(f: FormFideicomiso): ErroresFideicomiso {
  const errores: ErroresFideicomiso = {};
  if (!f.nombre.trim()) errores.nombre = "Poné el nombre del fideicomiso";
  const cuit = soloDigitosCuit(f.cuit);
  if (cuit && !/^\d{11}$/.test(cuit)) errores.cuit = "El CUIT tiene que tener 11 dígitos";
  const cuitFiduciario = soloDigitosCuit(f.fiduciario_cuit);
  if (cuitFiduciario && !/^\d{11}$/.test(cuitFiduciario)) errores.fiduciario_cuit = "El CUIT tiene que tener 11 dígitos";
  if (f.fecha_constitucion.trim() && parseFecha(f.fecha_constitucion) === null) {
    errores.fecha_constitucion = "Usá el formato dd/mm/aaaa";
  }
  const fee = feeNormalizado(f.fee_porcentaje);
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(fee) || Number(fee) > 100) {
    errores.fee_porcentaje = "Tiene que ser un porcentaje entre 0 y 100";
  }
  return errores;
}

const vacioANull = (s: string) => (s.trim() === "" ? null : s.trim());

/** Cuerpo del PATCH. Asume que `validarFideicomiso(f)` no devolvió errores. */
export function aPayload(f: FormFideicomiso) {
  return {
    nombre: f.nombre.trim(),
    cuit: vacioANull(soloDigitosCuit(f.cuit)),
    direccion: vacioANull(f.direccion),
    fecha_constitucion: f.fecha_constitucion.trim() ? parseFecha(f.fecha_constitucion) : null,
    fee_porcentaje: feeNormalizado(f.fee_porcentaje),
    banco: vacioANull(f.banco),
    cbu_alias_ars: vacioANull(f.cbu_alias_ars),
    cuenta_usd: vacioANull(f.cuenta_usd),
    fiduciario_nombre: vacioANull(f.fiduciario_nombre),
    fiduciario_cuit: vacioANull(soloDigitosCuit(f.fiduciario_cuit)),
  };
}

/** Alta de un desarrollo: sólo el nombre; el resto se completa en Configuración → Fideicomiso. */
export function validarNombreDesarrollo(nombre: string): string | null {
  if (!nombre.trim()) return "Poné el nombre del desarrollo";
  if (nombre.trim().length > 120) return "Hasta 120 caracteres";
  return null;
}
